/* src/utils/crypto.js */

// We use Web Crypto API (RSA-OAEP) for encryption
// Key size: 2048 bits
// Hash: SHA-256

export const generateKeyPair = async () => {
    try {
        const keyPair = await window.crypto.subtle.generateKey(
            {
                name: "RSA-OAEP",
                modulusLength: 2048,
                publicExponent: new Uint8Array([1, 0, 1]),
                hash: "SHA-256",
            },
            true,
            ["encrypt", "decrypt"]
        );
        return keyPair;
    } catch (error) {
        console.error("Error generating keys:", error);
        throw error;
    }
};

export const exportKey = async (key) => {
    try {
        const exported = await window.crypto.subtle.exportKey("jwk", key);
        return JSON.stringify(exported);
    } catch (error) {
        console.error("Error exporting key:", error);
        return null;
    }
};

export const importKey = async (jwkStr, type) => {
    try {
        const jwk = JSON.parse(jwkStr);
        return await window.crypto.subtle.importKey(
            "jwk",
            jwk,
            {
                name: "RSA-OAEP",
                hash: "SHA-256",
            },
            true,
            [type]
        );
    } catch (error) {
        console.error("Error importing key:", error);
        return null;
    }
};

/**
 * 💎 Hybrid Encryption (AES-GCM + RSA-OAEP)
 * Used for all text messages and file keys to support long content and cross-platform compatibility.
 */
export const encryptHybrid = async (message, publicKey, selfPublicKey = null) => {
    try {
        // 1. Generate Ephemeral AES Key (256-bit)
        const aesKey = await window.crypto.subtle.generateKey(
            { name: "AES-GCM", length: 256 },
            true,
            ["encrypt", "decrypt"]
        );

        // 2. Encrypt Content with AES
        const enc = new TextEncoder();
        const encodedMessage = enc.encode(message);
        const iv = window.crypto.getRandomValues(new Uint8Array(12));

        const encryptedContentBuffer = await window.crypto.subtle.encrypt(
            { name: "AES-GCM", iv: iv },
            aesKey,
            encodedMessage
        );

        const exportedAesKey = await window.crypto.subtle.exportKey("raw", aesKey);

        // 3. Encrypt AES Key with RSA (Recipient's Public Key)
        // Ensure publicKey is usable
        const encryptedKeyBuffer = await window.crypto.subtle.encrypt(
            { name: "RSA-OAEP" },
            publicKey, // Receive Key
            exportedAesKey
        );

        // 4. Encrypt AES Key for SELF (Sender) if provided
        let encryptedKeySelf = null;
        if (selfPublicKey) {
            const selfBuffer = await window.crypto.subtle.encrypt(
                { name: "RSA-OAEP" },
                selfPublicKey,
                exportedAesKey
            );
            encryptedKeySelf = btoa(String.fromCharCode(...new Uint8Array(selfBuffer)));
        }

        // 5. Construct JSON Payload
        // Format: { v: "hybrid-v1", k: <b64_enc_key>, sk: <b64_self_key>, iv: <b64_iv>, d: <b64_data> }
        const payload = {
            v: "hybrid-v1",
            k: btoa(String.fromCharCode(...new Uint8Array(encryptedKeyBuffer))),
            iv: btoa(String.fromCharCode(...iv)),
            d: btoa(String.fromCharCode(...new Uint8Array(encryptedContentBuffer)))
        };

        if (encryptedKeySelf) {
            payload.sk = encryptedKeySelf;
        }

        return JSON.stringify(payload);
    } catch (error) {
        console.error("Hybrid Encryption failed:", error);
        throw error;
    }
};

/**
 * Legacy RSA Encryption (Keep for specific short-data use cases if needed, but prefer Hybrid)
 */
export const encryptMessage = async (message, publicKey) => {
    // For backward compatibility or very short secrets, we can keep this.
    // However, for consistency, we might eventually route this to encryptHybrid too.
    // For now, let's strictly keep it as RSA-OAEP for legacy support.
    try {
        const enc = new TextEncoder();
        const encodedMessage = enc.encode(message);

        const ciphertext = await window.crypto.subtle.encrypt(
            { name: "RSA-OAEP" },
            publicKey,
            encodedMessage
        );

        return btoa(String.fromCharCode(...new Uint8Array(ciphertext)));
    } catch (error) {
        console.error("RSA Encryption failed:", error);
        throw error;
    }
};

export const decryptMessage = async (ciphertextBase64, privateKey) => {
    if (!ciphertextBase64 || !privateKey) return null;

    try {
        // 💎 Hybrid Decryption Check
        let isHybrid = false;
        let hybridJson = null;
        const trimmed = typeof ciphertextBase64 === 'string' ? ciphertextBase64.trim() : "";

        if (trimmed.startsWith('{')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (parsed.v === 'hybrid-v1' && parsed.k && parsed.iv && parsed.d) {
                    isHybrid = true;
                    hybridJson = parsed;
                }
            } catch (jsonErr) {
                console.warn("JSON Parse Failed:", jsonErr);
            }
        }

        if (isHybrid) {
            try {
                // 1. Decrypt AES Key
                let encKeyBuffer;

                // Try Primary Key (k)
                try {
                    encKeyBuffer = Uint8Array.from(atob(hybridJson.k), c => c.charCodeAt(0));
                } catch (e) { /* invalid base64 */ }

                let decryptedKeyBuffer;
                try {
                    decryptedKeyBuffer = await window.crypto.subtle.decrypt(
                        { name: "RSA-OAEP" },
                        privateKey,
                        encKeyBuffer
                    );
                } catch (primaryErr) {
                    // Failing to decrypt 'k' usually means I am the SENDER, not RECEIVER.
                    // Check if 'sk' (Self Key) exists and try that.
                    if (hybridJson.sk) {
                        try {
                            const skBuffer = Uint8Array.from(atob(hybridJson.sk), c => c.charCodeAt(0));
                            decryptedKeyBuffer = await window.crypto.subtle.decrypt(
                                { name: "RSA-OAEP" },
                                privateKey,
                                skBuffer
                            );
                        } catch (secondaryErr) {
                            throw primaryErr; // Both failed
                        }
                    } else {
                        throw primaryErr;
                    }
                }

                // 💎 IMPORT STRATEGY: Try RAW first (New Android), fallback to JWK (Old Android/Web)
                let aesKey;
                try {
                    aesKey = await window.crypto.subtle.importKey(
                        "raw",
                        decryptedKeyBuffer,
                        { name: "AES-GCM" },
                        true,
                        ["decrypt"]
                    );
                } catch (rawErr) {
                    // Fallback: Try JWK
                    const possibleJwk = new TextDecoder().decode(decryptedKeyBuffer);
                    const jwkObj = JSON.parse(possibleJwk);
                    aesKey = await window.crypto.subtle.importKey(
                        "jwk",
                        jwkObj,
                        { name: "AES-GCM" },
                        true,
                        ["decrypt"]
                    );
                }

                // 2. Decrypt Content
                const iv = Uint8Array.from(atob(hybridJson.iv), c => c.charCodeAt(0));
                const encryptedData = Uint8Array.from(atob(hybridJson.d), c => c.charCodeAt(0));

                const decryptedBuffer = await window.crypto.subtle.decrypt(
                    { name: "AES-GCM", iv: iv },
                    aesKey,
                    encryptedData
                );

                return new TextDecoder().decode(decryptedBuffer);

            } catch (e) {
                console.warn("Hybrid decryption failed:", e);
                return "🔒 Encrypted message";
            }
        }

        // 🛡️ Legacy RSA Decryption


        // Validation: If it has spaces, it's plaintext (Android fallback sometimes sends plain)
        if (typeof ciphertextBase64 === 'string' && (ciphertextBase64.includes(' ') || !ciphertextBase64.match(/^[A-Za-z0-9+/=]+$/))) {
            return ciphertextBase64;
        }

        const binaryString = atob(ciphertextBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        const decrypted = await window.crypto.subtle.decrypt(
            { name: "RSA-OAEP" },
            privateKey,
            bytes
        );

        return new TextDecoder().decode(decrypted);

    } catch (error) {
        // ⚠️ Failed to decode or decrypt
        if (error.name === 'OperationError') {
            // This is "normal" if the message was for another device/session.
            // Be silent or debug only to avoid spamming user console.
            // console.debug("Decryption skipped (key mismatch):", error.message);
            return "🔒 Encrypted message";
        } else if (error.name === 'InvalidCharacterError' || error.message.includes('correctly encoded')) {
            // Fallback: maybe it's text?
            return ciphertextBase64;
        } else {
            console.warn("Decryption failed:", error);
            return ciphertextBase64;
        }
    }
};

export const derivePublicKey = async (privateKeyJwkStr) => {
    try {
        const priv = JSON.parse(privateKeyJwkStr);
        // Construct public key JWK from private key components
        const pub = {
            kty: priv.kty,
            n: priv.n,
            e: priv.e,
            alg: priv.alg,
            ext: ["encrypt"],
            key_ops: ["encrypt"]
        };
        return JSON.stringify(pub);
    } catch (error) {
        console.error("Error deriving public key:", error);
        return null;
    }
};

// --- Password-Based Encryption (for Private Key Storage) ---

/**
 * Derives an AES-GCM key from a password and salt using PBKDF2
 */
const deriveKeyFromPassword = async (password, salt) => {
    const enc = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
        "raw",
        enc.encode(password),
        { name: "PBKDF2" },
        false,
        ["deriveKey"]
    );

    return await window.crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: salt,
            iterations: 100000,
            hash: "SHA-256",
        },
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
};

/**
 * Encrypts the Private Key JWK string using the user's password.
 * Returns object: { encrypted: base64, salt: base64, iv: base64 }
 */
export const encryptPrivateKey = async (privateKeyJwkString, password) => {
    try {
        const salt = window.crypto.getRandomValues(new Uint8Array(16));
        const iv = window.crypto.getRandomValues(new Uint8Array(12));

        const key = await deriveKeyFromPassword(password, salt);
        const enc = new TextEncoder();
        const encodedData = enc.encode(privateKeyJwkString);

        const encryptedBuffer = await window.crypto.subtle.encrypt(
            { name: "AES-GCM", iv: iv },
            key,
            encodedData
        );

        return {
            encrypted: btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer))),
            salt: btoa(String.fromCharCode(...salt)),
            iv: btoa(String.fromCharCode(...iv))
        };
    } catch (error) {
        console.error("Error encrypting private key:", error);
        throw error;
    }
};

/**
 * Decrypts the encrypted private key object using the user's password.
 * Expects encryptedData: { encrypted: base64, salt: base64, iv: base64 }
 * Returns: Private Key JWK string
 */
export const decryptPrivateKey = async (encryptedData, password) => {
    try {
        if (!encryptedData || !encryptedData.salt || !encryptedData.iv || !encryptedData.encrypted) {
            throw new Error("Invalid encrypted key format");
        }

        const salt = new Uint8Array(atob(encryptedData.salt).split("").map(c => c.charCodeAt(0)));
        const iv = new Uint8Array(atob(encryptedData.iv).split("").map(c => c.charCodeAt(0)));
        const data = new Uint8Array(atob(encryptedData.encrypted).split("").map(c => c.charCodeAt(0)));

        const key = await deriveKeyFromPassword(password, salt);

        const decryptedBuffer = await window.crypto.subtle.decrypt(
            { name: "AES-GCM", iv: iv },
            key,
            data
        );

        const dec = new TextDecoder();
        return dec.decode(decryptedBuffer);
    } catch (error) {
        console.error("Error decrypting private key:", error);
        throw error; // Propagate so UI can handle (e.g. show wrong password though auth usually handles that)
    }
};

// --- E2EE File Encryption (AES-GCM) ---

export const generateFileKey = async () => {
    return await window.crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
};

export const encryptFile = async (fileBlob, key) => {
    try {
        const iv = window.crypto.getRandomValues(new Uint8Array(12));
        const fileBuffer = await fileBlob.arrayBuffer();

        const encryptedBuffer = await window.crypto.subtle.encrypt(
            { name: "AES-GCM", iv: iv },
            key,
            fileBuffer
        );

        return {
            encryptedBlob: new Blob([encryptedBuffer]),
            iv: btoa(String.fromCharCode(...iv))
        };
    } catch (e) {
        console.error("File encryption failed:", e);
        throw e;
    }
};

export const decryptFile = async (encryptedBlob, keyJwk, ivBase64) => {
    try {
        const key = await window.crypto.subtle.importKey(
            "jwk",
            JSON.parse(keyJwk),
            { name: "AES-GCM" },
            true,
            ["decrypt"]
        );

        const iv = new Uint8Array(atob(ivBase64).split("").map(c => c.charCodeAt(0)));
        const buffer = await encryptedBlob.arrayBuffer();

        const decryptedBuffer = await window.crypto.subtle.decrypt(
            { name: "AES-GCM", iv: iv },
            key,
            buffer
        );

        return new Blob([decryptedBuffer]);
    } catch (e) {
        if (e.name === "OperationError") {
            throw new Error("File integrity check failed (possible corruption or tampering)");
        }
        throw e;
    }
};
